import { useEffect, useRef, useState, forwardRef, useImperativeHandle } from "react";
import { AudioMutedOutlined, AudioOutlined } from "@ant-design/icons";
import { CustomButton, showToaster, toasterTypes } from "../../widgets";
import { getFormVariables, getNonSurveyForm, uploadConversationAudioFiles, uploadSpeechToTextAudioFiles } from "./apiController";
import { agentNamesDataMap } from "../common/utils/aiAgentsData";
import { setLoading } from "../../store/slices/auth";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import useFieldItem from "../../widgets/fields/UseFieldItem";
import { cloneDeep } from "lodash";
import dayjs from "dayjs";
import { languagesData } from "../common/utils/languagesData";
import { FIELD_TYPES, FieldItem } from "../../widgets/fields";
import { SurveyFormElements } from "../surveyForm/formElements";

interface AudioFile {
    blob: Blob;
    startTime: number;
    endTime: number;
    duration: number;
    id: string;
}

interface RecordingStatus {
    isRecording: boolean;
    duration: number;
    fileCount: number;
}

interface ConversationRecordingProps {
    selectedFormData: any;
    numberOfSpeakers: any;
    promptMessage?: string;
    selectedLanguage?: any;
}

interface ConversationRecordingRef {
    uploadRecordings: () => Promise<any[]>;
    getRecordingStatus: () => RecordingStatus;
}

const ConversationRecording = forwardRef<ConversationRecordingRef>(() => {
    const location = useLocation();
    const props = location.state || {};

    const [formData, setFormData] = useState<any>(props?.selectedFormData || {});
    const [selectedLanguage, setSelectedLanguage] = useState<any>({ label: "English", value: "en" });
    const [promptMessage, setPromptMessage] = useState<string>("");
    const [numberOfSpeakers, setNumberOfSpeakers] = useState<any>(1);
    const [isTranslate, setIsTranslate] = useState<any>({ label: "Yes", value: true });
    const [uidFieldIds, setUidFieldIds] = useState<string[]>([]);

    const navigate = useNavigate();

    const FormElements = {
        fieldValue: {
            name: "fieldValue",
            label: "",
            isMandatory: true
        }
    }

    const [values, fields, handleChange, { validateValues, reset, updateValue, updateError, updateAllError, removeErrors }] = useFieldItem(FormElements, {}, { onValueChange });

    const gridFields = ["checkBoxGrid", "radioButtonGrid", "textBoxGrid"];


    const [recording, setRecording] = useState<boolean>(false);
    const [recordingDuration, setRecordingDuration] = useState<number>(0);
    const [fileCount, setFileCount] = useState<number>(0);

    const mediaStreamRef = useRef<MediaStream | null>(null);
    const audioContextRef = useRef<AudioContext | null>(null);
    const workletNodeRef = useRef<AudioWorkletNode | null>(null);
    const recordingTimerRef = useRef<NodeJS.Timeout | null>(null);
    const chunkTimerRef = useRef<NodeJS.Timeout | null>(null);
    const audioChunksRef = useRef<Int16Array[]>([]);
    const currentChunkStartTimeRef = useRef<number | null>(null);
    const audioFilesRef = useRef<AudioFile[]>([]);
    //ref to track if warning has been shown for current chunk
    const warningShownRef = useRef<boolean>(false);
    const historyInitializedRef = useRef(false);


    const MAX_CHUNK_DURATION = 60 * 60 * 1000; // 60 minutes
    const WARNING_THRESHOLD = 55 * 60 * 1000; // Warning at 55 minutes


    // VARIABLES DATA
    const [variables, setVariables] = useState<any[]>([]);

    const dispatch = useDispatch();

    // Fetch variables from API
    useEffect(() => {
        if (props.selectedFormData?.id) {
            fetchVariables();
        }
    }, [props.selectedFormData?.id]);


    const fetchVariables = async () => {
        try {
            dispatch(setLoading(true));

            const variablesResponse = await getFormVariables(props.selectedFormData?.id);
            const processedFields = processFormFields(variablesResponse.data || []);

            const uidFieldIds = processedFields
                .filter((field: any) =>
                    (field.field_use_type ?? "").includes("UID")
                )
                .map((field: any) => field.id);

            const response = await getNonSurveyForm(props.selectedFormData?.id, true);
            const nonSurveyProcessed = processFormFields(
                response.data.form_fields || []
            );

            const nonSurveyUidMap = new Map(
                nonSurveyProcessed
                    .filter((field: any) => uidFieldIds.includes(field.id))
                    .map((field: any) => [field.id, field])
            );

            const finalUidFields = uidFieldIds.map((id: any) => {
                return (
                    nonSurveyUidMap.get(id) ||
                    processedFields.find((f: any) => f.id === id)
                );
            }).filter(Boolean);

            const nonUidFields = processedFields.filter(
                (field: any) => !uidFieldIds.includes(field.id)
            );

            const finalFields = [...finalUidFields, ...nonUidFields];

            setVariables(finalFields);
            setUidFieldIds(uidFieldIds);

            dispatch(setLoading(false));
        } catch (error: any) {
            console.error("Error fetching variables:", error);
            showToaster(toasterTypes.ERROR, error?.message || "Failed to fetch variables");
            dispatch(setLoading(false));
        }
    };




    // Expose upload function to parent component
    // useImperativeHandle(ref, () => ({
    //     uploadRecordings: async (): Promise<UploadResult[]> => {
    //         if (recording) {
    //             await stopRecording();
    //         }
    //         return await uploadAllRecordings();
    //     },
    //     getRecordingStatus: (): RecordingStatus => ({
    //         isRecording: recording,
    //         duration: recordingDuration,
    //         fileCount: audioFilesRef.current.length + (audioChunksRef.current.length > 0 ? 1 : 0)
    //     })
    // }));

    const startRecording = async (): Promise<void> => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                audio: {
                    sampleRate: 16000,
                    channelCount: 1,
                    echoCancellation: false,
                    noiseSuppression: false,
                    autoGainControl: false
                }
            });

            const audioContext = new AudioContext({ sampleRate: 16000 });
            audioContextRef.current = audioContext;

            await audioContext.audioWorklet.addModule('/audio-processor.js');

            const source = audioContext.createMediaStreamSource(stream);
            const workletNode = new AudioWorkletNode(audioContext, 'pcm-processor');
            workletNodeRef.current = workletNode;

            audioChunksRef.current = [];
            currentChunkStartTimeRef.current = Date.now();
            warningShownRef.current = false;
            setFileCount(0);

            workletNode.port.onmessage = (event: MessageEvent) => {
                const float32Data = event.data as Float32Array;
                const int16Data = convertFloat32ToInt16(float32Data);
                audioChunksRef.current.push(int16Data);

                // Check if chunk duration exceeded on each audio data received
                if (currentChunkStartTimeRef.current !== null) {
                    const currentChunkDuration = Date.now() - currentChunkStartTimeRef.current;

                    // Check for warning
                    if (currentChunkDuration >= WARNING_THRESHOLD && !warningShownRef.current) {
                        const chunkNumber = audioFilesRef.current.length + 1;
                        showToaster(
                            toasterTypes.WARNING,
                            `Recording file ${chunkNumber} is about to complete 1 Hour.`, 10000
                        );
                        warningShownRef.current = true;
                    }

                    // Check if chunk should be split
                    if (currentChunkDuration >= MAX_CHUNK_DURATION) {
                        createNewChunk();
                    }
                }
            };

            source.connect(workletNode);
            mediaStreamRef.current = stream;
            setRecording(true);
            setRecordingDuration(0);

            // Start recording duration timer
            recordingTimerRef.current = setInterval(() => {
                setRecordingDuration(prev => prev + 1000);

                // Also check warning and chunk duration here as a backup
                if (currentChunkStartTimeRef.current !== null) {
                    const currentChunkDuration = Date.now() - currentChunkStartTimeRef.current;

                    // Check for warning
                    if (currentChunkDuration >= WARNING_THRESHOLD && !warningShownRef.current) {
                        const chunkNumber = audioFilesRef.current.length + 1;
                        showToaster(
                            toasterTypes.WARNING,
                            `Recording file ${chunkNumber} is about to complete 1 Hour.`, 10000
                        );
                        warningShownRef.current = true;
                    }

                    // Check if chunk should be split
                    if (currentChunkDuration >= MAX_CHUNK_DURATION) {
                        createNewChunk();
                    }
                }
            }, 1000);

        } catch (error) {
            console.error('Error starting recording:', error);
            showToaster(toasterTypes.ERROR, 'Failed to start recording. Please check microphone permissions.');
        }
    };

    const stopRecording = async (): Promise<void> => {
        return new Promise((resolve) => {
            // Clear timers FIRST before saving
            if (recordingTimerRef.current) {
                clearInterval(recordingTimerRef.current);
                recordingTimerRef.current = null;
            }

            // if (chunkTimerRef.current) {
            //     clearInterval(chunkTimerRef.current);
            //     chunkTimerRef.current = null;
            // }

            // Stop media stream
            if (mediaStreamRef.current) {
                mediaStreamRef.current.getTracks().forEach((track) => track.stop());
            }

            // Disconnect audio worklet
            if (workletNodeRef.current) {
                workletNodeRef.current.disconnect();
            }

            // Close audio context
            if (audioContextRef.current && audioContextRef.current.state !== "closed") {
                audioContextRef.current.close();
            }

            // Save current chunk if it has data AND hasn't been saved yet
            if (audioChunksRef.current.length > 0 && currentChunkStartTimeRef.current !== null) {
                saveCurrentChunk();
                // Reset these to prevent double-saving
                audioChunksRef.current = [];
                currentChunkStartTimeRef.current = null;
            }

            setRecording(false);
            resolve();
        });
    };

    const createNewChunk = (): void => {
        if (audioChunksRef.current.length > 0) {
            saveCurrentChunk();
            audioChunksRef.current = [];
            currentChunkStartTimeRef.current = Date.now();
            warningShownRef.current = false; // Reset warning flag for new chunk
        }
    };

    const saveCurrentChunk = (): void => {
        // Prevent duplicate saves
        if (currentChunkStartTimeRef.current === null || audioChunksRef.current.length === 0) {
            return;
        }

        const endTime = Date.now();
        const audioData = concatenateInt16Arrays(audioChunksRef.current);
        const audioBlob = createWavBlob(audioData, 16000, 1);

        const audioFile: AudioFile = {
            blob: audioBlob,
            startTime: currentChunkStartTimeRef.current,
            endTime: endTime,
            duration: endTime - currentChunkStartTimeRef.current,
            id: `chunk_${audioFilesRef.current.length + 1}_${currentChunkStartTimeRef.current}`
        };

        audioFilesRef.current.push(audioFile);
        setFileCount(audioFilesRef.current.length);

        currentChunkStartTimeRef.current = null;
    };

    const uploadAllRecordings = async () => {
        if (audioFilesRef.current.length === 0) {
            showToaster(toasterTypes.WARNING, 'No recordings to upload');
            return [];
        }

        dispatch(setLoading(true));

        try {
            const uploadPromises = audioFilesRef.current.map(async (audioFile: AudioFile): Promise<any> => {
                try {
                    const identifierId = "123";
                    const fileName = `${props?.selectedFormData?.projectData?.UserNumber
                        }_${props?.selectedFormData?.projectData?.id}_${audioFile.endTime.toString()}.wav`;

                    const formData = new FormData();
                    formData.append("file", audioFile.blob, fileName);

                    const token = localStorage.getItem('coguide_token') || '';
                    const aiAgents: any = JSON.parse(localStorage.getItem("aiAgents") || "[]");

                    let conversationAgent: any;
                    const languageCode = selectedLanguage?.value + "-IN" || 'en-IN';
                    const formId = props.selectedFormData?.id;
                    const is_translate = isTranslate?.value || false;
                    const durationInSeconds = Math.round(audioFile.duration / 1000);
                    const selectedUidFields = variables.filter((field) =>
                        uidFieldIds.includes(field.id)
                    );

                    const userIdentifiers = selectedUidFields.map((field) => {
                        if (
                            (field.field_type === "dropDown" ||
                                field.field_type === "dropDownPreviousResponses") &&
                            (field.field_use_type ?? "").includes("UID")
                        ) {
                            return {
                                "User_id_field_id": field.id,
                                "User_id": field.field_selected_value?.value ?? ""
                            };
                        }

                        return {
                            "User_id_field_id": field.id,
                            "User_id": field.field_selected_value ?? ""
                        };
                    });

                    if (is_translate) {
                        conversationAgent = aiAgents?.find((agent: any) => agent.name === agentNamesDataMap.speech_to_text_translate);
                    } else {
                        conversationAgent = aiAgents?.find((agent: any) => agent.name === agentNamesDataMap.speech_to_text_dialization);
                    }

                    const params = {
                        form_id: props.selectedFormData?.id,
                    };
                    formData.append('topic_id', props.selectedFormData?.projectData?.TopicID || '');
                    formData.append('no_of_speaker', (numberOfSpeakers?.value || 1).toString());
                    formData.append('prompt', promptMessage || '');
                    formData.append('is_conversation', 'true');
                    formData.append('language_code', languageCode);
                    formData.append('file_duration', durationInSeconds.toString());
                    formData.append('module_id', conversationAgent?.id || '');
                    formData.append('is_translate', is_translate.toString());
                    formData.append('token', token);
                    formData.append('User_identifiers', userIdentifiers.length > 0 ? JSON.stringify(userIdentifiers) : '');
                    formData.append('target_form_id', formId);

                    const response = await uploadSpeechToTextAudioFiles(formData, params);
                    // if (response?.status !== "received") {
                    //     throw new Error("Unexpected server response");
                    // }

                    return { success: true, audioFile, response };

                } catch (error) {
                    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
                    console.error(`Failed to upload ${audioFile.id}:`, error);
                    showToaster(toasterTypes.ERROR, `Failed to upload recording`);
                    return { success: false, audioFile, error: errorMessage };
                }
            });

            const results = await Promise.all(uploadPromises);

            const successfulUploads = results.filter(r => r.success);

            if (successfulUploads.length > 0) {
                showToaster(
                    toasterTypes.SUCCESS,
                    `Successfully uploaded ${successfulUploads.length} recording(s). Please check back later for transcription results.`
                );
            }

            audioFilesRef.current = [];
            setVariables([]);
            setFileCount(0); // Reset file count
            navigateToDataCollection();

        } finally {
            dispatch(setLoading(false));
        }
    };


    // Convert Float32 to Int16 PCM
    const convertFloat32ToInt16 = (buffer: Float32Array): Int16Array => {
        const l = buffer.length;
        const int16 = new Int16Array(l);
        for (let i = 0; i < l; i++) {
            const s = Math.max(-1, Math.min(1, buffer[i]));
            int16[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
        }
        return int16;
    };

    // Concatenate multiple Int16Arrays
    const concatenateInt16Arrays = (arrays: Int16Array[]): Int16Array => {
        const totalLength = arrays.reduce((sum, arr) => sum + arr.length, 0);
        const result = new Int16Array(totalLength);
        let offset = 0;

        for (const arr of arrays) {
            result.set(arr, offset);
            offset += arr.length;
        }

        return result;
    };

    // Create WAV blob from PCM data
    const createWavBlob = (pcmData: Int16Array, sampleRate: number, channels: number): Blob => {
        const byteLength = pcmData.length * 2;
        const buffer = new ArrayBuffer(44 + byteLength);
        const view = new DataView(buffer);

        // WAV header
        const writeString = (offset: number, string: string): void => {
            for (let i = 0; i < string.length; i++) {
                view.setUint8(offset + i, string.charCodeAt(i));
            }
        };

        writeString(0, 'RIFF');
        view.setUint32(4, 36 + byteLength, true);
        writeString(8, 'WAVE');
        writeString(12, 'fmt ');
        view.setUint32(16, 16, true);
        view.setUint16(20, 1, true);
        view.setUint16(22, channels, true);
        view.setUint32(24, sampleRate, true);
        view.setUint32(28, sampleRate * channels * 2, true);
        view.setUint16(32, channels * 2, true);
        view.setUint16(34, 16, true);
        writeString(36, 'data');
        view.setUint32(40, byteLength, true);

        // PCM data
        let offset = 44;
        for (let i = 0; i < pcmData.length; i++) {
            view.setInt16(offset, pcmData[i], true);
            offset += 2;
        }

        return new Blob([buffer], { type: 'audio/wav' });
    };

    // Format duration for display
    const formatDuration = (ms: number): string => {
        const seconds = Math.floor(ms / 1000);
        const minutes = Math.floor(seconds / 60);
        const hours = Math.floor(minutes / 60);

        if (hours > 0) {
            return `${hours}:${String(minutes % 60).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`;
        }
        return `${minutes}:${String(seconds % 60).padStart(2, '0')}`;
    };

    // Cleanup on component unmount ONLY
    useEffect(() => {
        return () => {
            if (recordingTimerRef.current) {
                clearInterval(recordingTimerRef.current);
            }

            if (mediaStreamRef.current) {
                mediaStreamRef.current.getTracks().forEach((track) => track.stop());
            }

            if (workletNodeRef.current) {
                workletNodeRef.current.disconnect();
            }

            if (audioContextRef.current && audioContextRef.current.state !== "closed") {
                audioContextRef.current.close();
            }
        };
    }, []);

    function onValueChange(name, value, values, fieldValues = null) {
        const tempFormFields: any = cloneDeep(variables);
        const fieldIndex = tempFormFields.findIndex((field) => field.id === name);
        const originalField = tempFormFields[fieldIndex];
        let updatedValue = value;

        const updatedField = {
            ...originalField,
            field_selected_value: updatedValue,
            localized_field_selected_value: updatedValue,
            data_entry_language: selectedLanguage.value,
        };

        const updatedFormFields = [...variables];
        updatedFormFields[fieldIndex] = updatedField;
        setVariables(updatedFormFields);

    }

    function getValues(data, type) {
        const valuesData: any[] = [];
        if (type === "prev") {
            data.forEach(item => {
                valuesData.push({
                    label: item.value,
                    value: item.value
                });
            });
        } else {
            data.forEach(item => {
                valuesData.push({
                    label: item,
                    value: item
                });
            });
        }
        return valuesData;
    }

    function processFormFields(formFieldsData) {
        const processedFields = formFieldsData.map(field => {
            const shouldTransform =
                field.field_type === "dropDown" ||
                field.field_type === "radioButton" ||
                field.field_type === "checkBox";

            const needsPrev = field.field_type === "dropDownPreviousResponses";
            const type = needsPrev ? "prev" : "";

            return {
                ...field,
                field_value: (shouldTransform || needsPrev)
                    ? getValues(field.field_value || [], type)
                    : field.field_value,

                localized_field_selected_value: null,
                data_entry_language: "en",

                localized: field.localized?.map(loc => ({
                    ...loc,
                    localized_field_value: shouldTransform
                        ? getValues(loc.localized_field_value || [], "")
                        : loc.localized_field_value,

                    localized_field_row_value: shouldTransform
                        ? getValues(loc.localized_field_row_value || [], "")
                        : loc.localized_field_row_value
                })) || []
            };
        });

        return processedFields;
    }

    const processFieldValue = (field: any, value, targetLanguage) => {
        // Handle null/undefined values
        if (value === null || value === undefined) {
            return {
                englishFieldValue: null,
                localizedFieldValue: null
            };
        }

        // Handle empty arrays for grid fields
        if (!value && gridFields.includes(field.field_type)) {
            return {
                englishFieldValue: [],
                localizedFieldValue: []
            };
        }

        // Handle empty strings or other falsy values
        if (!value) {
            return {
                englishFieldValue: value,
                localizedFieldValue: value
            };
        }

        // If field is not localized, return the value as is
        if (!field.is_localized) {
            if (["dateField", "dateTime"].includes(field.field_type) && value) {
                const isoDate = dayjs(value).toISOString();
                return {
                    englishFieldValue: isoDate,
                    localizedFieldValue: isoDate
                }
            }
            return {
                englishFieldValue: value,
                localizedFieldValue: value
            };
        }

        // Handle English target language - use field.field_value directly
        if (targetLanguage === 'en') {
            // If data was entered in English, return as is
            if (field.data_entry_language === 'en') {
                return {
                    englishFieldValue: value,
                    localizedFieldValue: value
                };
            }
            // If data was entered in another language, we need to convert to English
            // This will be handled in the switch cases below with targetLocalization = null
        }

        // Find the target language localization (will be null for English)
        const targetLocalization = targetLanguage === 'en' ? null : field.localized.find(loc => loc.language === targetLanguage);

        // If target language is not English and no localization found, return original values
        if (targetLanguage !== 'en' && (!targetLocalization || !targetLocalization.localized_field_value)) {
            return {
                englishFieldValue: value,
                localizedFieldValue: value
            };
        }

        // Find the data entry language localization to get the mapping
        const dataEntryLocalization = field.localized.find(loc => loc.language === field.data_entry_language);

        let targetFieldValue = value;
        let englishFieldValue = value;

        switch (field.field_type) {
            case "radioButton": {
                // Find index based on data entry language
                let sourceIndex = -1;
                if (field.data_entry_language === 'en') {
                    sourceIndex = field.field_value.findIndex(item => item.value === value);
                } else if (dataEntryLocalization) {
                    sourceIndex = dataEntryLocalization.localized_field_value.findIndex(item => item.value === value);
                }

                if (sourceIndex !== -1) {
                    englishFieldValue = field.field_value[sourceIndex].value;
                    // If target is English, use English value, otherwise use target localization
                    targetFieldValue = targetLanguage === 'en' ?
                        field.field_value[sourceIndex].value :
                        targetLocalization.localized_field_value[sourceIndex]?.value || value;
                }
                break;
            }

            case "dropDown": {
                let sourceIndex = -1;
                if (field.data_entry_language === 'en') {
                    sourceIndex = field.field_value.findIndex(item => {
                        if (typeof value === "object") {
                            return item.label === value.label && item.value === value.value;
                        }
                        return item.value === value;
                    });
                } else if (dataEntryLocalization) {
                    sourceIndex = dataEntryLocalization.localized_field_value.findIndex(item => {
                        if (typeof value === "object") {
                            return item.label === value.label && item.value === value.value;
                        }
                        return item.value === value;
                    });
                }

                if (sourceIndex !== -1) {
                    englishFieldValue = field.field_value[sourceIndex];
                    // If target is English, use English value, otherwise use target localization
                    targetFieldValue = targetLanguage === 'en' ?
                        field.field_value[sourceIndex] :
                        targetLocalization.localized_field_value[sourceIndex] || value;
                }
                break;
            }

            case "checkBox": {
                // Handle null or empty array
                if (!Array.isArray(value) || value.length === 0) {
                    englishFieldValue = [];
                    targetFieldValue = [];
                    break;
                }

                const targetSelected: any = [];
                const englishSelected: any = [];

                value.forEach((val: any) => {
                    let sourceIndex = -1;
                    if (field.data_entry_language === 'en') {
                        sourceIndex = field.field_value.findIndex(item => item.value === val);
                    } else if (dataEntryLocalization) {
                        sourceIndex = dataEntryLocalization.localized_field_value.findIndex(item => item.value === val);
                    }

                    if (sourceIndex !== -1) {
                        englishSelected.push(field.field_value[sourceIndex].value);
                        // If target is English, use English value, otherwise use target localization
                        targetSelected.push(targetLanguage === 'en' ?
                            field.field_value[sourceIndex].value :
                            targetLocalization.localized_field_value[sourceIndex]?.value || val);
                    } else {
                        englishSelected.push(val);
                        targetSelected.push(val);
                    }
                });

                englishFieldValue = englishSelected;
                targetFieldValue = targetSelected;
                break;
            }

            case "checkBoxGrid": {
                // Handle null or empty array
                if (!Array.isArray(value) || value.length === 0) {
                    englishFieldValue = [];
                    targetFieldValue = [];
                    break;
                }

                const targetSelected: any = [];
                const englishSelected: any = [];

                value.forEach(item => {
                    // Handle row mapping
                    let rowIndex = -1;
                    if (field.data_entry_language === 'en') {
                        rowIndex = field.field_row_value.findIndex(row => row === item.rowKey);
                    } else if (dataEntryLocalization) {
                        rowIndex = dataEntryLocalization.localized_field_row_value.findIndex(row => row === item.rowKey);
                    }

                    const englishRowKey = rowIndex !== -1 ? field.field_row_value[rowIndex] : item.rowKey;
                    const targetRowKey = targetLanguage === 'en' ?
                        englishRowKey :
                        (rowIndex !== -1 ? targetLocalization.localized_field_row_value[rowIndex] : item.rowKey);

                    // Handle column mapping
                    const englishCols: any = [];
                    const targetCols: any = [];

                    item.value.forEach(colVal => {
                        let colIndex = -1;
                        if (field.data_entry_language === 'en') {
                            colIndex = field.field_value.findIndex(col => col === colVal);
                        } else if (dataEntryLocalization) {
                            colIndex = dataEntryLocalization.localized_field_value.findIndex(col => col === colVal);
                        }

                        const englishCol = colIndex !== -1 ? field.field_value[colIndex] : colVal;
                        const targetCol = targetLanguage === 'en' ?
                            englishCol :
                            (colIndex !== -1 ? targetLocalization.localized_field_value[colIndex] : colVal);

                        englishCols.push(englishCol);
                        targetCols.push(targetCol);
                    });

                    englishSelected.push({ rowKey: englishRowKey, value: englishCols });
                    targetSelected.push({ rowKey: targetRowKey, value: targetCols });
                });

                englishFieldValue = englishSelected;
                targetFieldValue = targetSelected;
                break;
            }

            case "radioButtonGrid": {
                // Handle null or empty array
                if (!Array.isArray(value) || value.length === 0) {
                    englishFieldValue = [];
                    targetFieldValue = [];
                    break;
                }

                const targetSelected: any = [];
                const englishSelected: any = [];

                value.forEach(item => {
                    // Handle row mapping
                    let rowIndex = -1;
                    if (field.data_entry_language === 'en') {
                        rowIndex = field.field_row_value.findIndex(row => row === item.rowKey);
                    } else if (dataEntryLocalization) {
                        rowIndex = dataEntryLocalization.localized_field_row_value.findIndex(row => row === item.rowKey);
                    }

                    // Handle column mapping
                    let colIndex = -1;
                    if (field.data_entry_language === 'en') {
                        colIndex = field.field_value.findIndex(col => col === item.value);
                    } else if (dataEntryLocalization) {
                        colIndex = dataEntryLocalization.localized_field_value.findIndex(col => col === item.value);
                    }

                    const englishRowKey = rowIndex !== -1 ? field.field_row_value[rowIndex] : item.rowKey;
                    const englishColVal = colIndex !== -1 ? field.field_value[colIndex] : item.value;

                    const targetRowKey = targetLanguage === 'en' ?
                        englishRowKey :
                        (rowIndex !== -1 ? targetLocalization.localized_field_row_value[rowIndex] : item.rowKey);
                    const targetColVal = targetLanguage === 'en' ?
                        englishColVal :
                        (colIndex !== -1 ? targetLocalization.localized_field_value[colIndex] : item.value);

                    englishSelected.push({ rowKey: englishRowKey, value: englishColVal });
                    targetSelected.push({ rowKey: targetRowKey, value: targetColVal });
                });

                englishFieldValue = englishSelected;
                targetFieldValue = targetSelected;
                break;
            }

            case "textBoxGrid": {
                // Handle null or empty array
                if (!Array.isArray(value) || value.length === 0) {
                    englishFieldValue = [];
                    targetFieldValue = [];
                    break;
                }

                const targetSelected: any = [];
                const englishSelected: any = [];

                value.forEach(item => {
                    const { rowKey, colKey, value: cellValue } = item;

                    // Handle row mapping
                    let rowIndex = -1;
                    if (field.data_entry_language === 'en') {
                        rowIndex = field.field_row_value.findIndex(row => row === rowKey);
                    } else if (dataEntryLocalization) {
                        rowIndex = dataEntryLocalization.localized_field_row_value.findIndex(row => row === rowKey);
                    }

                    // Handle column mapping
                    let colIndex = -1;
                    if (field.data_entry_language === 'en') {
                        colIndex = field.field_value.findIndex(col => col === colKey);
                    } else if (dataEntryLocalization) {
                        colIndex = dataEntryLocalization.localized_field_value.findIndex(col => col === colKey);
                    }

                    const englishRowKey = rowIndex !== -1 ? field.field_row_value[rowIndex] : rowKey;
                    const englishColKey = colIndex !== -1 ? field.field_value[colIndex] : colKey;

                    const targetRowKey = targetLanguage === 'en' ?
                        englishRowKey :
                        (rowIndex !== -1 ? targetLocalization.localized_field_row_value[rowIndex] : rowKey);
                    const targetColKey = targetLanguage === 'en' ?
                        englishColKey :
                        (colIndex !== -1 ? targetLocalization.localized_field_value[colIndex] : colKey);

                    englishSelected.push({ rowKey: englishRowKey, colKey: englishColKey, value: cellValue });
                    targetSelected.push({ rowKey: targetRowKey, colKey: targetColKey, value: cellValue });
                });

                englishFieldValue = englishSelected;
                targetFieldValue = targetSelected;
                break;
            }

            default: {
                // For other field types, return as is
                englishFieldValue = value;
                targetFieldValue = value;
                break;
            }
        }
        return {
            englishFieldValue: englishFieldValue,
            localizedFieldValue: targetFieldValue
        };
    };

    // Get localized form name based on selected language
    const getLocalizedFormName = (form) => {
        if (selectedLanguage?.value === 'en' || !form.is_localized) {
            return form?.form_name;
        }

        const localization = form.localized.find(loc => loc.language === selectedLanguage.value);
        return localization?.localized_value || form?.form_name;
    };

    const getLocalizedFieldName = (field) => {
        if (selectedLanguage?.value === 'en' || !field?.is_localized) {
            return field?.field_name;
        }

        const localization = field.localized.find(loc => loc.language === selectedLanguage.value);
        return localization?.localized_value || field.field_name;
    };


    const getDisplayValue = (field) => {
        const { englishFieldValue, localizedFieldValue }: any = processFieldValue(field, field.field_selected_value, selectedLanguage.value || 'en');

        return localizedFieldValue || englishFieldValue || field.field_selected_value || "";
    };

    // Get localized field values (options) based on selected language
    const getLocalizedFieldValues = (field) => {
        if (selectedLanguage?.value === 'en' || !field.is_localized) {
            return field.field_value;
        }

        const localization = field.localized.find(loc => loc.language === selectedLanguage.value);
        return localization?.localized_field_value || field.field_value;
    };

    // Get localized field values (options) based on selected language
    const getLocalizedFieldRowValues = (field) => {
        if (selectedLanguage?.value === 'en' || !field.is_localized) {
            return field.field_row_value;
        }
        const localization = field.localized.find(loc => loc.language === selectedLanguage.value);
        return localization?.localized_field_row_value || field.field_row_value;
    };

    const getLanguageValues = () => {
        let temp: any = formData;
        const formLanguages = temp?.localized || [];
        const otherLanguages = languagesData.filter(
            lang =>
                lang.value !== "en" && formLanguages.some(i => i.language === lang.value)
        );
        const english = languagesData.find(lang => lang.value === "en");
        return english ? [english, ...otherLanguages] : otherLanguages;
    };

    const handleLanguageChange = (e) => {
        setSelectedLanguage(e);
    };

    const handleTranslateChange = (e) => {
        setIsTranslate(e);
    }

    const getUidField = (type = "value") => {
        const uidField = variables
            ?.flatMap(fields => fields)
            .find(field => field.field_use_type.includes("UID"));

        if (!uidField || !uidField.field_selected_value) {
            return type === "exists" ? false : "";
        }

        if (type === "exists") {
            return true;
        }

        if (type === "name") {
            return uidField.field_name || "";
        }

        const val = uidField.field_selected_value;

        if (typeof val === "object") {
            return type === "value" ? val.value || "" : "";
        }

        // If it's a string, return directly
        return val;
    };

    const navigateToDataCollection = () => {
        navigate(window.innerWidth > 750 ? "/dataCollection" : "/dataCollectionMobile", { state: props?.projectData });
    }

    const hasUnsavedData = () => {
        return (
            recording ||
            audioFilesRef.current.length > 0 ||
            variables.some(v => v.field_selected_value)
        );
    };

    const confirmExit = () => {
        if (!hasUnsavedData()) return true;

        return window.confirm(
            "You have an ongoing recording or unsaved data. Leaving this page will permanently discard it. Do you want to continue?"
        );
    };

    useEffect(() => {
        const handlePopState = () => {
            if (!hasUnsavedData()) return;

            const shouldLeave = window.confirm(
                "You have an ongoing recording or unsaved data. Leaving this page will permanently discard it. Do you want to continue?"
            );

            if (!shouldLeave) {
                // User canceled → restore current page
                window.history.pushState(null, "", window.location.href);
            } else {
                // User confirmed → navigate back programmatically
                navigate(-1);
            }
        };

        if (!historyInitializedRef.current) {
            window.history.pushState(null, "", window.location.href);
            historyInitializedRef.current = true;
        }

        window.addEventListener("popstate", handlePopState);

        return () => {
            window.removeEventListener("popstate", handlePopState);
        };
    }, [recording, variables]);


    return (
        <>
            <div className="speech-to-text-conversation-container">

                <div className='close-container'>
                    <i
                        className="icon icon-close"
                        onClick={async () => {
                            if (!confirmExit()) return;

                            if (recording) {
                                await stopRecording();
                            }

                            navigateToDataCollection();
                        }}
                    />
                </div>
                {/* <div className='coguide-banner-container'>
                    {formData?.banner_image ? <img src={`https://go.coguide.in/c1/download/${formData.banner_image}`} alt='preview-banner' className='banner' /> : <img src={`${process.env.PUBLIC_URL}/images/survey_tools.svg`} alt='preview-banner' className='banner' />}
                </div> */}
                <div className='coguide-logo-container'>
                    <p className="speech-title">SPEECH TO TEXT CONVERSATION RECORDING</p>
                    <img src={`${process.env.PUBLIC_URL}/images/powered_by_coguide2.png`} alt='logo' className='logo' />
                </div>
                <div className='name-container'>
                    <div className='p-0 left-container'>
                        <div className='form-name'>{getLocalizedFormName(formData)}</div>
                        <div className='form-description'>{formData?.form_desc}</div>
                        {getUidField("exists") && <div className='selected-uid'>{getUidField("name")} : {getUidField("value")}</div>}
                    </div>
                    {<div className="right-container">
                        <div className='language'>
                            <FieldItem
                                {...SurveyFormElements.language}
                                values={getLanguageValues()}
                                isMandatory={false}
                                value={selectedLanguage}
                                onChange={(...e) => handleLanguageChange(...e)}
                            />
                        </div>
                        <div className='number-of-speakers'>
                            <FieldItem
                                {...SurveyFormElements.number_of_speakers}
                                value={numberOfSpeakers}
                                onChange={(e) => {
                                    setNumberOfSpeakers(e);
                                }}
                            />
                        </div>
                        <div className='is-translate'>
                            <FieldItem
                                {...SurveyFormElements.is_translate}
                                value={isTranslate}
                                isMandatory={false}
                                onChange={(...e) => handleTranslateChange(...e)}
                            />
                        </div>
                        {isTranslate?.value && <div className='prompt-message'>
                            <FieldItem
                                {...SurveyFormElements.prompt_message}
                                value={promptMessage}
                                onChange={(e) => {
                                    typeof e === "string" && setPromptMessage(e);
                                }}
                            />
                        </div>}

                        <div className="conversation-recording-controls">
                            <div
                                className={`microphone ${recording ? 'recording' : ''}`}
                                onClick={recording ? stopRecording : startRecording}
                            >
                                {recording ? (
                                    <>
                                        <AudioMutedOutlined />
                                        <span>Stop Recording</span>
                                    </>
                                ) : (
                                    <>
                                        <AudioOutlined />
                                        <span>Start Conversation Recording</span>
                                    </>
                                )}
                            </div>

                            {/* {recording && (
                                <div className="recording-info">
                                    <div>Duration: {formatDuration(recordingDuration)}</div>
                                    <div>Files: {fileCount + 1}</div>
                                </div>
                            )} */}

                            {audioFilesRef.current.length > 0 && !recording && (
                                <div className="recording-summary">
                                    <div>{audioFilesRef.current.length} recording file(s) ready for upload</div>
                                </div>
                            )}
                        </div>

                    </div>}
                </div>


                {/* Variables List */}
                <h3 className="stt-variables-to-fill">Variables to Fill</h3>
                <div className="stt-variables-list">

                    {variables.length === 0 ? (
                        <p>No variables to display</p>
                    ) : (
                        variables.map((obj, index) => (
                            <div className="field-body">
                                <FieldItem
                                    key={index}
                                    name={obj.id}
                                    placeholder={
                                        obj.field_type === "number" ||
                                            obj.field_type === "textBox" ||
                                            obj.field_type === "textArea"
                                            ? "Enter"
                                            : "Select"
                                    }
                                    isDisabled={!uidFieldIds.includes(obj.id)} // Disable if not in UID fields
                                    label={obj.field_type === "instruction" ? "" : getLocalizedFieldName(obj)}
                                    type={
                                        obj.field_type === "fileUpload"
                                            ? FIELD_TYPES.FILE_UPLOAD
                                            : obj.field_type === "dateField"
                                                ? FIELD_TYPES.DATEPICKER_NEW
                                                : obj.field_type === "dropDown"
                                                    ? FIELD_TYPES.DROP_DOWN
                                                    : obj.field_type === "dropDownPreviousResponses"
                                                        ? FIELD_TYPES.DROP_DOWN
                                                        : obj.field_type === "boolean"
                                                            ? FIELD_TYPES.BOOLEAN
                                                            : obj.field_type === "radioButton"
                                                                ? FIELD_TYPES.RADIO_BUTTON
                                                                : obj.field_type === "checkBox"
                                                                    ? FIELD_TYPES.CHECK_BOX
                                                                    : obj.field_type === "textArea"
                                                                        ? FIELD_TYPES.TEXT_AREA
                                                                        : obj.field_type === "textBox"
                                                                            ? FIELD_TYPES.TEXT
                                                                            : obj.field_type === "time"
                                                                                ? FIELD_TYPES.TIME_PICKER
                                                                                : obj.field_type === "dateTime"
                                                                                    ? FIELD_TYPES.DATE_TIME_PICKER
                                                                                    : obj.field_type === "signatureCapture"
                                                                                        ? FIELD_TYPES.SIGNATURE_CAPTURE
                                                                                        : obj.field_type === "imageCapture"
                                                                                            ? FIELD_TYPES.IMAGE_CAPTURE
                                                                                            : obj.field_type === "videoCapture"
                                                                                                ? FIELD_TYPES.VIDEO_CAPTURE
                                                                                                : obj.field_type === "audioCapture"
                                                                                                    ? FIELD_TYPES.AUDIO_CAPTURE
                                                                                                    : obj.field_type === "instruction"
                                                                                                        ? FIELD_TYPES.INPUT_INSTRUCTION
                                                                                                        : obj.field_type === "checkBoxGrid"
                                                                                                            ? FIELD_TYPES.CHECK_BOX_GRID
                                                                                                            : obj.field_type === "radioButtonGrid"
                                                                                                                ? FIELD_TYPES.RADIO_BUTTON_GRID
                                                                                                                : obj.field_type === "textBoxGrid"
                                                                                                                    ? FIELD_TYPES.TEXT_BOX_GRID
                                                                                                                    : obj.field_type === "fileUploadOCR"
                                                                                                                        ? FIELD_TYPES.FILE_UPLOAD
                                                                                                                        : FIELD_TYPES.TEXT
                                    }
                                    isNumber={obj.field_type === "number" ? true : false}
                                    value={obj.field_type === "instruction" ? getLocalizedFieldName(obj) : getDisplayValue(obj)}
                                    values={getLocalizedFieldValues(obj)}
                                    rowValues={getLocalizedFieldRowValues(obj)}
                                    isListed={true}
                                    onChange={(...e) => {
                                        handleChange(obj.id, ...e);
                                    }}
                                />
                            </div>
                        ))
                    )}
                </div>

                {/* Submit Button */}
                {variables.length > 0 && (
                    <div className="submit-controls">
                        <CustomButton
                            text="Submit Recording"
                            type="primary"
                            onClick={uploadAllRecordings}
                            isDisabled={audioFilesRef.current.length === 0 || recording}
                        />
                    </div>
                )}
            </div>
        </>
    );
});

ConversationRecording.displayName = 'ConversationRecording';

export default ConversationRecording;
// export type { ConversationRecordingRef, ConversationRecordingProps, AudioFile, UploadResult, RecordingStatus };
